import { DimensionsMixin } from "@nrk/element-dimensions-mixin";
import { FocusVisibleMixin } from "@nrk/element-focus-visible-mixin";
import { FocusWithinController } from "@nrk/element-focus-within-controller";
import { ShadowPropertiesMixin } from "@nrk/element-shadow-properties-mixin";
import { version as playerCoreVersion } from "@nrk/player-core/types.js";
import { guessMediaTypeOfUrl, toError, unlockMediaElement } from "@nrk/player-core/utils.js";
import { PSAPIClient } from "@nrk/player-psapi-client";
import { ReactiveElement, ReactiveState } from "@nrk/reactive-element";
import { getActionFromEvent, getFontSize } from "../utils/dom.js";
import { PlayerPanicHandlerMixin } from "../utils/element-panic-handler-mixin.js";
import { ElementPanicMixin } from "../utils/element-panic-mixin.js";
import { getLogger } from "../utils/logging.js";
import { addPerformanceMark } from "../utils/performance.js";
import platform from "../utils/platform.js";
import { preloadImage } from "../utils/preload.js";
import { parseMetadata } from "./metadata-parser.js";
import { PlaybackStateMixin } from "./playback-state-mixin.js";
import { PlayerActions } from "./player-actions.js";
import { PlayerError, PlayerErrorTypes } from "./player-error.js";
import { PlayerEvents } from "./player-events.js";
import { SubtitleState } from "./subtitles-state.js";
const SHORT_PROGRAM_LIMIT = 120;
const DEFAULT_SEEK_STEP = 15;
const SHORT_PROGRAM_SEEK_STEP = 5;
const FONT_SIZE_BREAK_POINT = 500;
const PLAYER_ELEMENTS_VERSION = "11.0.0";
class PlayerElement extends DimensionsMixin(
  FocusVisibleMixin(
    PlaybackStateMixin(ShadowPropertiesMixin(PlayerPanicHandlerMixin(ElementPanicMixin(ReactiveElement))))
  )
) {
  static get reactiveAttributes() {
    return {
      ...super.reactiveAttributes,
      blockedContentAge: Number,
      autoplay: Boolean,
      // list of feature flags enabled for player
      features: Array,
      loop: Boolean,
      preferredManifest: String,
      preferredSubtitle: String,
      preferredBandwidth: String,
      // muted - added by Playback-State mixin
      src: String,
      psapiOrigin: String,
      psapiRecommendationsOrigin: String,
      startTime: (element, propertyValue) => {
        if (!propertyValue || propertyValue.toString() === "undefined") {
          return;
        }
        if (isDate(propertyValue)) {
          element.startTimeLive = new Date(propertyValue);
        } else {
          element.startTimeOndemand = Number(propertyValue);
        }
      },
      cdnOverride: String,
      snowplowAppId: String,
      snowplowUrl: String,
      snowplowServiceId: String,
      volume: Number
    };
  }
  static get reactiveProperties() {
    return {
      ...super.reactiveProperties,
      // dimensions - added by Dimensions mixin
      // epg state for live buffers
      epgState: ReactiveState,
      // fullscreenState - added by Playback-State mixin
      interactionPointState: Object,
      // isCurrentTimeLive - added by Playback-State mixin
      // isEnded - added by Playback-State mixin
      // isLive - added by Playback-State mixin
      // isPlaying - added by Playback-State mixin
      // isRealtime - added by Playback-State mixin
      // isStarted - added by Playback-State mixin
      // isTimeRunning - added by Playback-State mixin
      // isWaiting - added by Playback-State mixin
      // focusVisible - added by FocusVisible mixin
      // manifest - added by Playback-State mixin
      // seekMode - added by Playback-State mixin
      metadata: Object,
      // pipState - added by Playback-State mixin
      // playbackRate - added by Playback-State mixin
      poster: String,
      // airPlayState - added by Playback-State mixin
      resolvedSrc: String,
      resolvedType: String,
      // seekableStep - added by Playback-State mixin
      // subtitleState - added by Playback-State mixin
      speak: String,
      // volume - added by Playback-State mixin
      //used by FocusWitinController
      focusWithin: Boolean,
      theaterMode: Boolean,
      subtitlesHidden: Boolean,
      startTimeOndemand: Number,
      startTimeLive: Date,
      user: Object
    };
  }
  // dimensions - added by Dimensions mixin
  // fullscreenState - added by Playback-State mixin
  interactionPointState;
  // isCurrentTimeLive - added by Playback-State mixin
  // isEnded - added by Playback-State mixin
  // isLive - added by Playback-State mixin
  // isPlaying - added by Playback-State mixin
  // isRealtime - added by Playback-State mixin
  // isSeeking - added by Playback-State mixin
  // isStarted - added by Playback-State mixin
  // isTimeRunning - added by Playback-State mixin
  // isWaiting - added by Playback-State mixin
  // focusVisible - added by FocusVisible mixin
  /** The current Metadata instance */
  metadata;
  // pipState - added by Playback-State mixin
  // playbackRate - added by Playback-State mixin
  /** The current poster source reference */
  poster = "";
  // airPlayState - added by Playback-State mixin
  // delay (in seconds) first skip button display by this much to avoid clash with controls
  // instance variable to be able to override in players
  startupSkipPointDelay = 0;
  /** The media source reference resolved to a valid href */
  resolvedSrc;
  /** The media source type */
  resolvedType;
  // seekableStep - added by Playback-State mixin
  /** A message to speak via aria-live */
  speak = "";
  // subtitleState - added by Playback-State mixin
  // volume - added by Playback-State mixin
  // activeSession - added by Playback-State mixin
  /** Flag to trigger playing as soon as "src" has been set */
  autoplay = false;
  /** EPG State object */
  epgState;
  /** unleash feature flags */
  features = [];
  /** used by FocusWithinController */
  focusWithin = false;
  focusWithinController;
  /** Force usage of one cdn */
  cdnOverride;
  /** preferred manifest version to play */
  preferredManifest = void 0;
  /** preferred subtitle */
  preferredSubtitle = void 0;
  /** preferred bandwidth used in psapi calls to override bandwidth requirements */
  preferredBandwidth;
  /** snowplow app id */
  snowplowAppId;
  /** snowplow service id, needs to be set per player */
  snowplowServiceId = "n/a";
  /** snowplow platform, needs to be set per player */
  snowplowPlatform = "web";
  /** snowplow collector url, defaults to none */
  snowplowUrl = "";
  /** are we trying to start playback? */
  isAwaitingPlaybackStart = false;
  /** are we in binge mode or not */
  isBinging = false;
  /** Flag representing player suspension state */
  isSuspended = false;
  pendingMetadataResponse;
  pendingMetadataPlayable;
  /** The PSAPI client instance */
  psapiClient;
  /** Origin url for psapi. default to prod */
  psapiOrigin = "https://psapi.nrk.no";
  /** Origin url for psapi recommendations. default to prod */
  psapiRecommendationsOrigin = "https://anbefaling-psapi.nrk.no";
  /** The PlayerCore instance for this player, lazily initialised */
  player;
  /** The name, version and player family of this player, for tracking purposes */
  playerName = "";
  playerVersionNumber = "n/a";
  playerFamily = "player-core";
  /** Flag to trigger re-play once playback has ended */
  loop = false;
  // muted - added by Playback-State mixin
  /** The initial playhead position in seconds */
  startTimeOndemand;
  /** The initial playhead position for live streams, a timestamp/date */
  startTimeLive;
  /** The media source reference */
  src = "";
  /** UserState object supplied from context */
  user;
  /** Theater mode for player */
  theaterMode;
  /** are subtitles hidden or not */
  subtitlesHidden = false;
  /** Playback state when player suspended with call to "suspend()" */
  savedPlaybackState;
  _speakClearTimeoutId = 0;
  _countdownIntervalId = 0;
  /** The Player-Core logger instance */
  logger = getLogger("Player");
  constructor() {
    super();
    this.recommendNextInteractionPointHandler = this.recommendNextInteractionPointHandler.bind(this);
    this.skipToInteractionPointHandler = this.skipToInteractionPointHandler.bind(this);
    this.nextUpInteractionPointHandler = this.nextUpInteractionPointHandler.bind(this);
    this.getAuthToken = this.getAuthToken.bind(this);
    this.focusWithinController = new FocusWithinController(this, {
      onChanged: () => {
        this.focusWithin = this.focusWithinController.value;
        this.classList.toggle("focus-within", this.focusWithin);
      }
    });
  }
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("change", this);
    this.addEventListener("click", this);
    this.addEventListener("input", this);
  }
  readyCallback() {
    if (!this.src) {
      const source = getSourceFromElement(this);
      if (source) {
        const { src, type } = source;
        this.resolvedType = type;
        this.src = src;
      }
    }
    if (platform.isMobile) {
      this.classList.add("mobile");
    }
  }
  /**
   * Clean up when element removed from DOM
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.destroySession();
    this.psapiClient?.abort();
    this.psapiClient = void 0;
    this.removeEventListener("click", this);
    this.removeEventListener("input", this);
    this.removeEventListener("change", this);
  }
  updating(propName, oldValue) {
    super.updating(propName, oldValue);
    if (propName === "volume") {
      this.activeSession?.setVolume(this.volume);
    } else if (propName === "muted") {
      this.activeSession?.[this.muted ? "mute" : "unmute"]();
    } else if (propName === "activeSession") {
      this.activeSession?.setVolume(this.volume);
    } else if (propName === "metadata" && this.metadata) {
      this.isLive = this.metadata.isLive ?? false;
      if (this.preferredManifest !== void 0) {
        const preferredManifest = this.metadata?.manifests?.find((a) => a.type === this.preferredManifest);
        const defaultManifest = this.metadata?.manifests?.find((a) => a.type === "default");
        if (preferredManifest && !this.src.endsWith(preferredManifest.id)) {
          this.src = `nrk:${this.isLive ? "channel" : "program"}:${preferredManifest.id}`;
          this.pendingMetadataPlayable = void 0;
        } else if (!preferredManifest && defaultManifest) {
          this.src = `nrk:${this.isLive ? "channel" : "program"}:${defaultManifest.id}`;
          this.pendingMetadataPlayable = void 0;
        }
      }
    } else if (propName === "manifest" && this.manifest && "playable" in this.manifest) {
      const source = getSourceFromManifest(this.manifest);
      if (source) {
        this.resolvedSrc = source.src;
        this.resolvedType = source.type;
      }
    } else if (propName === "preferredManifest") {
      const isManifestReset = oldValue !== void 0;
      if (isManifestReset) {
        this.manifest = void 0;
        this.metadata = void 0;
        this.psapiClient?.abort();
        this.psapiClient = void 0;
        this.resolvedSrc = void 0;
        this.isSuspended = false;
        if (this.savedPlaybackState) {
          this.startTimeOndemand = this.savedPlaybackState.startTime;
        } else {
          this.startTimeOndemand = void 0;
        }
        this.stop();
        if (PSAPIClient.isSupported(this.src)) {
          this.psapiClient = this.initPsapiClient();
          this.loadMetadata();
        } else {
          this.resolvedSrc = this.src;
        }
      }
    } else if (propName === "src") {
      const isSrcReset = oldValue !== void 0;
      if (isSrcReset) {
        this.manifest = void 0;
        this.metadata = void 0;
        this.psapiClient?.abort();
        this.psapiClient = void 0;
        this.resolvedSrc = void 0;
        this.isSuspended = false;
        if (this.savedPlaybackState) {
          this.startTimeOndemand = this.savedPlaybackState.startTime;
        } else {
          this.startTimeOndemand = void 0;
        }
        this.stop();
      }
      if (this.src && this.src !== "undefined") {
        if (PSAPIClient.isSupported(this.src)) {
          this.psapiClient = this.initPsapiClient();
          this.loadMetadata();
        } else {
          this.resolvedSrc = this.src;
        }
      }
    } else if (propName === "isRealtime") {
      this.isRealtime ? this.classList.add("realtime") : this.classList.remove("realtime");
    } else if (propName === "isLive") {
      this.isLive ? this.classList.add("live") : this.classList.remove("live");
    } else if (propName === "isEnded" && this.isEnded) {
      this.startTimeOndemand = void 0;
    } else if (propName === "playbackRate") {
      this.activeSession?.setPlaybackRate(this.playbackRate);
    } else if (propName === "dimensions" && this.dimensions) {
      this.adjustFontSize();
    }
  }
  initPsapiClient() {
    const playbackOptions = {
      origin: this.psapiOrigin,
      playerFamily: this.playerFamily,
      enableEeaPortability: true
    };
    const reccomendationsOptions = {
      origin: this.psapiRecommendationsOrigin
    };
    if (this.user?.id) {
      playbackOptions.authTokenFunction = this.getAuthToken;
      playbackOptions.contentGroup = this.user?.contentGroup;
    }
    playbackOptions.ageRestriction = this.user?.ageRestriction;
    if (this.cdnOverride) {
      playbackOptions.cdnOverride = this.cdnOverride;
    }
    if (this.preferredBandwidth) {
      playbackOptions.preferredBandwidth = this.preferredBandwidth;
    }
    this.pendingMetadataPlayable = void 0;
    this.pendingMetadataResponse = void 0;
    return new PSAPIClient(this.src, this.playerName, {
      playback: playbackOptions,
      recommendations: reccomendationsOptions
    });
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    const isChangedSrc = changedProperties.has("src");
    const isValidSrc = this.src && this.src !== "undefined";
    const shouldSuspend = (this.activeSession !== void 0 || this.isAwaitingPlaybackStart) && changedProperties.has("hidden") && this.hidden;
    const shouldResume = changedProperties.has("hidden") && !this.hidden && !isChangedSrc && isValidSrc;
    const shouldPlay = isChangedSrc && isValidSrc && (changedProperties.get("isPlaying") === true || this.autoplay);
    if (isChangedSrc) {
      this.interactionPointState = void 0;
    }
    if (shouldSuspend) {
      this.suspend();
    } else if (shouldResume) {
      this.resume();
    } else if (shouldPlay) {
      this.play();
    }
    if (changedProperties.has("isStarted") && this.isStarted) {
      addPerformanceMark("playback-startup-time-end");
    }
  }
  /**
   * Handle events registered in "connectedCallback"
   */
  handleEvent(event) {
    if (event.defaultPrevented) {
      return;
    }
    const [action, value] = getActionFromEvent(event);
    if (!action) {
      return;
    }
    switch (action) {
      case PlayerActions.EnterFullscreen:
        event.preventDefault();
        this.activeSession?.enterFullscreen();
        break;
      case PlayerActions.ExitFullscreen:
        event.preventDefault();
        this.activeSession?.exitFullscreen();
        break;
      case PlayerActions.EnterPip:
        event.preventDefault();
        this.hideSubtitles(true);
        if (platform.isSafari) {
          this.activeSession?.displaySubtitles(true);
        }
        this.activeSession?.enterPiP();
        break;
      case PlayerActions.ExitPip:
        event.preventDefault();
        if (platform.isSafari) {
          this.activeSession?.displaySubtitles(false);
        }
        this.hideSubtitles(false);
        this.activeSession?.exitPiP();
        break;
      case PlayerActions.EnterTheaterMode:
        event.preventDefault();
        this.theaterMode = true;
        this.dispatchEvent(
          new CustomEvent(PlayerEvents.EnterTheaterMode, {
            bubbles: true,
            cancelable: true
          })
        );
        break;
      case PlayerActions.ExitTheaterMode:
        event.preventDefault();
        this.theaterMode = false;
        this.dispatchEvent(
          new CustomEvent(PlayerEvents.ExitTheaterMode, {
            bubbles: true,
            cancelable: true
          })
        );
        break;
      case PlayerActions.EnterAirPlay:
        event.preventDefault();
        this.activeSession?.enterAirPlay();
        break;
      case PlayerActions.Play:
        event.preventDefault();
        this.play();
        break;
      case PlayerActions.Pause:
        event.preventDefault();
        this.pause();
        break;
      case PlayerActions.TogglePlay:
        event.preventDefault();
        this.togglePlay();
        break;
      case PlayerActions.Mute:
        event.preventDefault();
        this.muted = true;
        break;
      case PlayerActions.Unmute:
        event.preventDefault();
        this.muted = false;
        break;
      case PlayerActions.StopPlayNextCountdown:
      case PlayerActions.DismissRecommendationsForProgramCountdown:
        this.interactionPointState = void 0;
        window.clearInterval(this._countdownIntervalId);
        break;
      case PlayerActions.PlayNextEpisode:
        this.abortInteractionPoint();
        this.playNext();
        break;
      case PlayerActions.ToggleSubtitles: {
        event.preventDefault();
        this.subtitleState?.toggleVisibility();
        break;
      }
      case PlayerActions.ToggleMute: {
        event.preventDefault();
        this.toggleMute();
        break;
      }
      case PlayerActions.SelectStream: {
        if (value !== this.src) {
          this.savedPlaybackState = { startTime: this.activeSession?.getCurrentTime() };
          this.src = value;
          this.dispatchEvent(
            new CustomEvent(PlayerEvents.StreamChange, {
              bubbles: true,
              cancelable: true,
              detail: { type: this.preferredManifestType }
            })
          );
        }
        break;
      }
      case PlayerActions.SetPoster: {
        event.preventDefault();
        if (value) {
          this.poster = value;
        }
        break;
      }
      case PlayerActions.SetVolume: {
        event.preventDefault();
        if (value !== void 0) {
          const parsedValue = typeof value === "string" ? parseFloat(value) : value;
          this.volume = Math.min(Math.max(parsedValue / 100, 0), 1);
          this.muted = this.volume === 0;
        }
        break;
      }
      case PlayerActions.SeekTo: {
        event.preventDefault();
        this.interactionPointState = void 0;
        if (value !== void 0) {
          this.seekTo(value);
        }
        break;
      }
      case PlayerActions.SeekToLive: {
        event.preventDefault();
        this.activeSession?.seekToLive();
        break;
      }
      case PlayerActions.SeekToRelativeTime: {
        event.preventDefault();
        if (value !== void 0) {
          this.seekToRelativeTime(value);
        }
        break;
      }
      case PlayerActions.SeekToPosition: {
        event.preventDefault();
        if (value !== void 0) {
          this.seekToPosition(value);
        }
        break;
      }
      case PlayerActions.SkipToPoint: {
        this.seekTo(value);
        break;
      }
      case PlayerActions.Speak: {
        event.preventDefault();
        if (value) {
          this.speak = value;
          clearTimeout(this._speakClearTimeoutId);
          this._speakClearTimeoutId = window.setTimeout(() => {
            this.speak = "";
          }, 2e3);
        }
        break;
      }
    }
  }
  /**
   * The current media playhead position in seconds
   */
  get currentTime() {
    return this.activeSession?.getCurrentTime() ?? 0;
  }
  hideSubtitles(hidden) {
    this.subtitlesHidden = hidden;
  }
  /**
   * Preload media before playback
   */
  preload(options) {
    return this.activeSession?.preload(options);
  }
  /**
   * Begin media playback
   */
  async play() {
    if (this.isAwaitingPlaybackStart) {
      return;
    }
    if (this.isSuspended) {
      return this.resume();
    }
    this.isWaiting = !this.player || !this.activeSession;
    if (!this.player) {
      try {
        await this.initPlayerCoreInstance();
      } catch (err) {
        this.isWaiting = false;
        this.panic(err);
        return;
      }
    }
    if (!this.activeSession) {
      this.isAwaitingPlaybackStart = true;
      this.unlockAudioVideoElement();
      if (PSAPIClient.isSupported(this.src)) {
        await this.loadMetadata();
      }
      if (this.metadata && !this.metadata.availability.available) {
        this.stop();
        return;
      }
      if (!this.resolvedSrc) {
        await this.loadManifest(this.preferredManifestType);
        addPerformanceMark("playback-startup-time-start");
      }
      if (this.resolvedSrc) {
        await this.createSession();
      }
      this.isAwaitingPlaybackStart = false;
      if (!this.activeSession || this.isSuspended) {
        this.isWaiting = false;
        this.stop();
        return;
      }
    }
    try {
      await this.activeSession.play();
    } catch (err) {
      this.isWaiting = false;
      if (toError(err).name !== "AutoplayError") {
        this.panic(err);
      }
    }
  }
  /**
   * Pause media playback
   */
  pause() {
    if (this.activeSession?.isPlaying()) {
      this.activeSession.pause();
    }
  }
  /**
   * Toggle media playback state
   */
  togglePlay() {
    if (!this.activeSession || !this.activeSession.isPlaying()) {
      return this.play();
    }
    this.pause();
  }
  /**
   * Suspend media playback
   */
  async suspend(replayOnResume) {
    this.isSuspended = true;
    if (this.activeSession) {
      this.savedPlaybackState = {
        // Trigger play if re-playing ended media
        isPlaying: this.isEnded || this.isPlaying || replayOnResume,
        startTime: this.isStarted ? this.currentTime : this.startTimeOndemand
      };
    }
    return this.stop();
  }
  /**
   * Resume previously suspended playback
   */
  async resume() {
    this.isSuspended = false;
    if (this.savedPlaybackState) {
      const savedPlaybackState = this.savedPlaybackState;
      this.startTimeOndemand = savedPlaybackState.startTime;
      this.savedPlaybackState = void 0;
      await this.play();
      if (!savedPlaybackState.isPlaying) {
        this.pause();
        this.isStarted = true;
      }
    } else {
      this.play();
    }
  }
  /**
   * Stop media playback
   */
  stop() {
    this.isAwaitingPlaybackStart = false;
    this.psapiClient?.abort();
    return this.destroySession();
  }
  /**
   * Toggle media mute state
   */
  toggleMute() {
    this.muted = !this.muted;
  }
  /**
   * Reset playbackRate to default of 1.0
   */
  resetPlaybackRate() {
    this.activeSession?.resetPlaybackRate();
  }
  /**
   * Seek to absolute "time" in seconds
   */
  seekTo(time) {
    if (!this.activeSession) {
      return;
    }
    this.isSeeking = true;
    if (typeof time === "string") {
      time = parseFloat(time);
    }
    this.activeSession.seekTo(time);
  }
  /**
   * Seek to position using date
   */
  seekToDate(date) {
    if (!this.activeSession) {
      return;
    }
    this.isSeeking = true;
    this.seekTo(this.activeSession.liveTimeToPosition(date));
  }
  /**
   * Seek to "time" with relative offset
   */
  seekToRelativeTime(time) {
    if (!this.activeSession) {
      return;
    }
    this.isSeeking = true;
    if (typeof time === "string") {
      time = parseFloat(time);
    }
    this.activeSession.seekToRelativeTime(time);
  }
  /**
   * Seek to "position" from 0 (start) to 1 (end) of current seekable range
   */
  seekToPosition(position) {
    if (!this.activeSession) {
      return;
    }
    this.isSeeking = true;
    if (typeof position === "string") {
      position = parseFloat(position);
    }
    const range = this.activeSession.getSeekableRange();
    const time = position * (range.end - range.start) + range.start;
    this.activeSession.seekTo(time);
  }
  /**
   * Dispatch custom player event
   */
  emit(type, detail) {
    this.dispatchEvent(new CustomEvent(type, { bubbles: false, detail }));
  }
  recommendNextInteractionPointHandler(event) {
    const interactionPoint = event.range.data ? { ...event.range.data } : null;
    if (event.type === "enter") {
      if (!interactionPoint) {
        this.dispatchEvent(
          new CustomEvent(PlayerEvents.TrackRecommendationsForProgram, {
            bubbles: true,
            detail: {
              type: "noContent"
            }
          })
        );
        return;
      }
      if (!this.features.includes("recommendations_a_b_test")) {
        this.dispatchEvent(
          new CustomEvent(PlayerEvents.TrackRecommendationsForProgram, {
            bubbles: true,
            detail: {
              type: "notEligible"
            }
          })
        );
        return;
      }
      this.interactionPointState = {
        type: "recommendNext",
        point: interactionPoint
      };
      this._countdownIntervalId = window.setInterval(() => {
        if (this.interactionPointState?.type === "recommendNext") {
          const point = this.interactionPointState.point;
          const currentCountDownDuration = point.countdownDuration;
          this.interactionPointState = {
            ...this.interactionPointState,
            point: {
              ...point,
              countdownDuration: currentCountDownDuration && Number.isInteger(currentCountDownDuration) && currentCountDownDuration > 0 ? currentCountDownDuration - 1 : void 0
            }
          };
          if (this.interactionPointState.point.countdownDuration === 0) {
            window.clearInterval(this._countdownIntervalId);
          }
        }
      }, 1e3);
    } else if (event.type === "exit") {
      if (this.interactionPointState?.type !== "recommendNext") {
        return;
      }
      this.interactionPointState = void 0;
    }
  }
  skipToInteractionPointHandler(event) {
    const interactionPoint = event.range.data;
    if (event.type === "enter") {
      this.interactionPointState = {
        type: "skipTo",
        point: interactionPoint
      };
    } else if (event.type === "exit") {
      this.interactionPointState = void 0;
      this.isBinging = false;
    }
  }
  nextUpInteractionPointHandler(event) {
    const interactionPoint = event.range.data;
    if (event.type === "enter") {
      this.isBinging = true;
      this.interactionPointState = {
        type: "nextUp",
        point: interactionPoint
      };
      this._countdownIntervalId = window.setInterval(() => {
        if (this.interactionPointState?.type === "nextUp") {
          const point = this.interactionPointState.point;
          const currentCountDownDuration = point.countdownDuration;
          this.interactionPointState = {
            ...this.interactionPointState,
            point: {
              ...point,
              countdownDuration: currentCountDownDuration && Number.isInteger(currentCountDownDuration) && currentCountDownDuration > 0 ? currentCountDownDuration - 1 : void 0
            }
          };
          if (this.interactionPointState.point.countdownDuration === 0) {
            window.clearInterval(this._countdownIntervalId);
          }
        }
      }, 1e3);
    } else if (event.type === "exit") {
      if (this.interactionPointState?.type !== "nextUp") {
        return;
      }
      if (this.interactionPointState.point.countdownDuration) {
        this.abortInteractionPoint();
        return this.playNext();
      }
      if (this.interactionPointState.point.manualExtension && this.currentTime < event.range.start) {
        this.interactionPointState = void 0;
        this.isBinging = false;
      }
    }
  }
  /**
   * Extend updateComplete if waiting for "metadata" since it is a derived value of "src"
   */
  get updateComplete() {
    return new Promise((resolve) => {
      super.updateComplete.then(() => {
        return this.pendingMetadataResponse;
      }).then(() => {
        resolve(this.isUpdatePending);
      });
    });
  }
  _onMediaInfoLoaded(event) {
    super._onMediaInfoLoaded(event);
    this.addEventsForInteractionPoints();
  }
  /**
   * Handle panicking errors dispatched by player or child elements
   */
  handlePanic(error) {
    super.handlePanic(error);
    this.suspend();
  }
  /**
   * Determine if this player can play given media type
   */
  canPlayType(_mediaType) {
    throw Error('PlayerElement subclass must implement "canPlayType()" method based on required PlayerCore adapters');
  }
  playerVersion() {
    return `${this.playerName}@${this.playerVersionNumber} (player-elements@${PLAYER_ELEMENTS_VERSION}/player-core@${playerCoreVersion})`;
  }
  abortInteractionPoint() {
    if (this.interactionPointState?.type === "recommendNext") {
      this.dispatchEvent(new Event(PlayerEvents.DismissRecommendationsForProgramCountdown, { bubbles: true }));
    }
    this.interactionPointState = void 0;
    window.clearInterval(this._countdownIntervalId);
  }
  resetNextUpCountdown() {
    if (this.interactionPointState?.type === "nextUp") {
      this.interactionPointState.point.countdownDuration = this.interactionPointState.point.originalCountdownDuration;
    }
  }
  abortNextUpCountdown() {
    if (this.interactionPointState?.type === "nextUp") {
      window.clearInterval(this._countdownIntervalId);
      this.interactionPointState.point.countdownDuration = void 0;
      this.interactionPointState.point.displayValue = "Neste";
      this.interactionPointState.point.manualExtension = true;
      this.isBinging = false;
    }
  }
  abortRecommendNextCountdown() {
    if (this.interactionPointState?.type === "recommendNext") {
      window.clearInterval(this._countdownIntervalId);
      this.interactionPointState.point.countdownDuration = void 0;
      this.interactionPointState.point.manualExtension = true;
    }
  }
  /**
   * Retrieve @nrk/player-core module and instantiate 'player' instance
   */
  async initPlayerCoreInstance() {
    throw Error('PlayerElement subclass must implement "initPlayerCoreInstance()" method');
  }
  async createSession() {
    await this.destroySession();
    if (!this.resolvedSrc) {
      return this.panic(
        new PlayerError(
          PlayerErrorTypes.MISSING_SOURCE,
          true,
          new Error("Avspilling feilet p\xE5 grunn av manglende mediakilde")
        )
      );
    }
    const mediaType = (this.resolvedType ? { type: this.resolvedType } : guessMediaTypeOfUrl(this.resolvedSrc)) ?? {
      type: "video/mp4"
    };
    if (!this.canPlayType(mediaType)) {
      return this.panic(
        new PlayerError(
          PlayerErrorTypes.SOURCE_NOT_SUPPORTED,
          true,
          new Error("Avspilling av mediainnholdet er ikke st\xF8ttet p\xE5 denne enheten"),
          { mediaId: this.src, mediaUrl: this.resolvedSrc }
        )
      );
    }
    try {
      const initialState = {
        mute: this.muted,
        startTime: this.metadata?.isChannel ? void 0 : this.startTimeOndemand,
        startTimeLive: this.metadata?.isChannel ? this.startTimeLive : void 0,
        videoPoster: this.poster,
        // @ts-expect-error: Used by cast-player-core (CastInitialSessionState).
        preferredManifest: this.preferredManifest
      };
      const mediaId = this.src !== this.resolvedSrc ? this.src : void 0;
      const session = await this.player.createPlaybackSession(
        {
          id: mediaId,
          // Needed by CastPlayerCore/Chromecast.
          url: this.resolvedSrc,
          mediaType
        },
        initialState
      );
      if (this.activeSession !== session) {
        this._observePlaybackSession(session);
      }
    } catch (err) {
      this.panic(err);
    }
  }
  destroySession() {
    if (this.activeSession) {
      const session = this.activeSession;
      this._unobservePlaybackSession(session);
      this.epgState?.destroy();
      this.epgState = void 0;
      return session.close();
    }
    return Promise.resolve();
  }
  /**
   * Load metadata from PSAPI
   */
  async loadMetadata(throwNonPlayableError = true) {
    if (!this.psapiClient) {
      this.panic(Error(`Unable to load metadata for invalid PSAPI media reference: ${this.src}`));
      return;
    }
    if (this.metadata) {
      return;
    }
    if (this.pendingMetadataPlayable) {
      return this.pendingMetadataPlayable;
    }
    const iterator = this.psapiClient.loadMetadata({ throwNonPlayableError });
    let resolvePlayability;
    this.pendingMetadataPlayable = new Promise((resolve) => resolvePlayability = resolve);
    const iterate = () => {
      this.pendingMetadataResponse = iterator.next().then((result) => {
        this.pendingMetadataResponse = void 0;
        if (!result.done) {
          this.metadata = parseMetadata(result.value, this.startTimeOndemand);
          iterate();
        } else {
          resolvePlayability();
          this.pendingMetadataPlayable = void 0;
        }
      }).catch((err) => {
        this.pendingMetadataPlayable = void 0;
        this.pendingMetadataResponse = void 0;
        if (toError(err).name !== "AbortError") {
          this.panic(err);
        }
      });
    };
    iterate();
    return this.pendingMetadataPlayable;
  }
  async loadManifest(manifestType) {
    if (!this.psapiClient) {
      this.panic(Error(`Unable to load manifest for invalid PSAPI media reference: ${this.src}`));
      return;
    }
    if (this.pendingMetadataPlayable) {
      await this.pendingMetadataPlayable;
    }
    try {
      const manifestResponse = await this.psapiClient.updateManifest(manifestType);
      this.manifest = parseManifest(manifestResponse.data);
    } catch (err) {
      if (toError(err).name !== "AbortError") {
        this.panic(err);
      }
    }
  }
  /**
   * Unlock the media element, but check for the presence of one first.
   * Autoplay policy on ios safari needs this to not get a autoplayblocked on first click
   */
  unlockAudioVideoElement() {
    const element = this.querySelector("video,audio");
    if (element) {
      unlockMediaElement(element);
    }
  }
  /**
   * Common implementation for selecting default subtitles.
   */
  selectDefaultSubtitles() {
    const available = this.activeSession?.getAvailableSubtitles() ?? [];
    const subtitles = available.find((sub) => sub.flags.isDefault);
    if (subtitles !== void 0) {
      this.activeSession?.selectSubtitles(subtitles);
    } else {
      this.activeSession?.selectNoSubtitles();
    }
  }
  adjustFontSize() {
    this.style.setProperty("--player-base-font-size", this.getRemFontSize());
  }
  /**
   * Find an appropriate font size based on the size of the player.
   * Scaling factor is 2 for small players, 1.25 for large players
   * Find a scaled base font size for the root element and divide it by the
   * actual font size of the root element, to take the user preference into account
   */
  getRemFontSize() {
    const scale = this.dimensions.width < FONT_SIZE_BREAK_POINT ? 1.9 : 1.25;
    const absoluteSize = scale * this.baseFontSize();
    const relativeSize = absoluteSize / getFontSize(document.documentElement);
    return `${relativeSize}rem`;
  }
  /**
   * The base font size is relative to the width of the largest possible 16:9
   * rectangle inside the the player. At 800px width, the font size is 1rem (in
   * pixels).
   *
   * The reference size could be 1rem at any width, but 800px seems to provide
   * a reasonable base size to work with.
   *
   * Using the root font size enables scaling of the text inside Ludo by
   * adjusting the root font size. This is for accessibility.
   */
  baseFontSize() {
    const maxAspectRatio = 1 / 0.5625;
    const referenceSize = getFontSize(document.documentElement) / 800;
    const baseWidth = Math.min(this.dimensions.width, this.dimensions.height * maxAspectRatio);
    return baseWidth * referenceSize;
  }
  get preferredManifestType() {
    const preferredManifest = this.metadata?.manifests?.find((v) => v.type === this.preferredManifest);
    if (preferredManifest) {
      return preferredManifest.type;
    } else if (this.src.match(/TOLK/)) {
      return "tegntolk";
    } else if (this.src.match(/SYNS/)) {
      return "synstolk";
    }
    return "default";
  }
  addEventsForInteractionPoints() {
    if (this.metadata?.interactionPoints?.seekToPoints && this.activeSession) {
      const timeRangeEvents = this.activeSession.createTimeRangeEventTrack();
      for (const seekToPoint of this.metadata.interactionPoints.seekToPoints) {
        this.logger.log("adding TimeRangeEvent for seekToPoint", seekToPoint);
        const startTime = seekToPoint.startTime === 0 ? this.startupSkipPointDelay : seekToPoint.startTime;
        const range = timeRangeEvents?.addRange(
          { start: startTime, end: seekToPoint.endTime },
          {
            type: "seekTo",
            displayValue: seekToPoint.displayValue,
            seekToTime: seekToPoint.seekToTime,
            visible: true,
            autoskipInBingeMode: seekToPoint.autoSkipInBingeMode
          }
        );
        range?.on("enter", this.skipToInteractionPointHandler);
        range?.on("exit", this.skipToInteractionPointHandler);
      }
      if (this.metadata?.interactionPoints?.nextUpPoint && this.metadata.nextSrc) {
        const nextUpPoint = this.metadata.interactionPoints.nextUpPoint;
        this.logger.info("adding TimeRangeEvent for nextUpPoint", nextUpPoint);
        const range = timeRangeEvents?.addRange(
          { start: nextUpPoint.startTime, end: this.metadata.duration },
          {
            type: "nextUp",
            displayValue: "Neste om",
            visible: true,
            startTime: nextUpPoint.startTime,
            countdownDuration: nextUpPoint.countDownDuration,
            originalCountdownDuration: nextUpPoint.countDownDuration
          }
        );
        range?.on("enter", this.nextUpInteractionPointHandler);
        range?.on("exit", this.nextUpInteractionPointHandler);
      }
      const isRecommendNextEnabled = this.features.includes("tv_smart_tizen_player_show_recommendations");
      if (isRecommendNextEnabled && this.metadata?.interactionPoints?.recommendNextPoint && !this.metadata?.interactionPoints.nextUpPoint) {
        const recommendNextPoint = this.metadata.interactionPoints.recommendNextPoint;
        this.logger.info("adding TimeRangeEvent for recommendNextPoint", recommendNextPoint);
        const metadata = this.metadata;
        this.psapiClient?.loadRecommendationsForProgram().then((recommendationsResponse) => {
          const recommendationsData = recommendationsResponse.data;
          const data = recommendationsData && recommendationsData.recommendations.length ? {
            title: "Se noe mer?",
            subtitle: "Forslag vises om",
            images: recommendationsData.recommendations.map(
              (recommendation) => recommendation.images.mainKeyArt.map((item) => {
                return {
                  uri: item.url,
                  width: item.width
                };
              })
            ),
            countdownDuration: 4,
            recommendationsForProgram: recommendationsData,
            // Data we will emit from the player so that consumers can use them
            manualExtension: false
          } : null;
          const range = timeRangeEvents?.addRange(
            { start: recommendNextPoint.startTime, end: metadata.duration + 10 },
            data
          );
          range?.on("enter", this.recommendNextInteractionPointHandler);
          range?.on("exit", this.recommendNextInteractionPointHandler);
          data?.images.map((image) => {
            preloadImage(image);
          });
        }).catch((error) => {
          this.logger.error("Failed to load recommendations:", error);
        });
      }
    }
  }
  playNext() {
    if (this.metadata?.nextSrc) {
      this.dispatchEvent(
        new CustomEvent(PlayerEvents.PlayNextEpisode, {
          bubbles: true,
          cancelable: true,
          detail: {
            src: this.metadata?.nextSrc
          }
        })
      );
      this.interactionPointState = void 0;
      this.isBinging = true;
    }
  }
  getAuthToken() {
    if (this.user?.id) {
      return this.user.getAuthToken().then((token) => {
        return Promise.resolve(token);
      }).catch((_error) => {
        return Promise.resolve(void 0);
      });
    }
    return Promise.resolve(void 0);
  }
  // for easier debugging/testing, throwing a random error to enclosing context
  fakeError(type, fatal = false) {
    this.dispatchEvent(
      new CustomEvent(PlayerEvents.PlayerError, {
        bubbles: true,
        cancelable: true,
        detail: new PlayerError(
          type,
          fatal,
          type ? new Error(`FakeError ${type}`) : new Error(
            `fakeError(errorType: string | PlayerErrorTypes, fatal = false)
Throw an error out of the player to any listening context. Error types can be:
- ${Object.keys(
              PlayerErrorTypes
            ).join("\n- ")}
`
          )
        )
      })
    );
  }
}
function parseManifest(manifest) {
  if (manifest.playable) {
    const thumbnail2D = manifest.playable.thumbnail2D ?? void 0;
    return {
      href: manifest._links.self.href,
      statistics: manifest.statistics,
      playable: manifest.playable,
      thumbnail2D
    };
  }
}
function getSourceFromManifest(manifest) {
  const assets = manifest.playable.assets;
  if (assets && assets.length) {
    const asset = assets[0];
    return { src: asset.url, type: asset.mimeType };
  }
}
function getSourceFromElement(element) {
  for (const { src, type } of Array.from(element.querySelectorAll("source"))) {
    if (element.canPlayType({ type })) {
      return { src, type };
    }
  }
}
function isDate(value) {
  return isNaN(Number(value));
}
export {
  DEFAULT_SEEK_STEP,
  PLAYER_ELEMENTS_VERSION,
  PlayerActions,
  PlayerElement,
  PlayerErrorTypes,
  SHORT_PROGRAM_LIMIT,
  SHORT_PROGRAM_SEEK_STEP,
  SubtitleState
};
